import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, ActivityIndicator, Platform, Modal, Dimensions } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import { Business } from '../types/business';
import { favoritesService } from '../services/favoritesService';
import UserBottomMenu from '../components/UserBottomMenu';
import { BUSINESS_CATEGORIES } from '../constants/businessCategories';
import AppointmentRequestForm from '../components/AppointmentRequestForm';
import TransportationQuoteRequestForm from '../components/TransportationQuoteRequestForm';
import { isTransportationBusinessCategory } from '../utils/transportationCategoryUtils';
import { getGuestZipcodeAsync, setGuestZipcodeAsync } from '../utils/guestSession';

interface BusinessExplorationScreenProps {
  onBack: () => void;
  onBusinessSelect?: (business: Business) => void;
  onBookBusiness?: (business: Business) => void;
  onTabPress?: (tab: 'dashboard' | 'appointments' | 'favorites' | 'profile') => void;
  onLogout?: () => void;
  /** Try-before-register: browse without account; zip stored in guest session. */
  guestMode?: boolean;
  onSignIn?: () => void;
}

const BusinessExplorationScreen: React.FC<BusinessExplorationScreenProps> = ({
  onBack,
  onBusinessSelect,
  onBookBusiness,
  onTabPress,
  onLogout,
  guestMode = false,
  onSignIn,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all'); // Default to "All Categories"
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(false);
  const [userZipcode, setUserZipcode] = useState<string>('');
  const [favoriteBusinessIds, setFavoriteBusinessIds] = useState<Set<string>>(new Set());
  const [searchRadius, setSearchRadius] = useState<number>(5); // Default 5 miles
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [selectedBusinessForRequest, setSelectedBusinessForRequest] = useState<Business | null>(null);
  const [requestType, setRequestType] = useState<'appointment' | 'estimate'>('appointment');
  const [searchQuery, setSearchQuery] = useState<string>(''); // Search bar input
  const [screenWidth, setScreenWidth] = useState(Dimensions.get('window').width);
  // When traveling: zipcode to search in (overrides profile). Null = use profile location.
  const [searchZipcodeOverride, setSearchZipcodeOverride] = useState<string | null>(null);
  const [showChangeAreaModal, setShowChangeAreaModal] = useState(false);
  const [changeAreaZipcode, setChangeAreaZipcode] = useState('');
  const [changeAreaError, setChangeAreaError] = useState<string | null>(null);
  /** Profile address country — used with postal/PIN for validation and geocoding */
  const [userCountryForLocation, setUserCountryForLocation] = useState<string>('');
  const [showZipcodePrompt, setShowZipcodePrompt] = useState(false);
  const [promptZipcode, setPromptZipcode] = useState('');
  const [promptZipcodeError, setPromptZipcodeError] = useState<string | null>(null);
  const [savingPromptZipcode, setSavingPromptZipcode] = useState(false);

  const EXPLORE_ZIP_SKIP_KEY = 'bookapt_explore_zip_skipped';

  // Responsive grid: columns by screen size
  const getColumnsCount = () => {
    if (screenWidth >= 1400) return 5;
    if (screenWidth >= 1100) return 4;
    if (screenWidth >= 768) return 3;
    return 2;
  };
  const columnsCount = getColumnsCount();
  const cardWidth = `${100 / columnsCount - 2}%`;

  // Update screen width on resize (web)
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      const onResize = () => {
        if (typeof window.innerWidth !== 'undefined') {
          setScreenWidth(window.innerWidth);
        }
      };
      window.addEventListener('resize', onResize);
      return () => {
        if (typeof window.removeEventListener === 'function') {
          window.removeEventListener('resize', onResize);
        }
      };
    }
  }, []);

  // Use centralized categories with "All" option
  const categories = [
    { id: 'all', name: 'All Categories', icon: '🏪', description: 'Show all businesses' },
    ...BUSINESS_CATEGORIES,
  ];

  // Removed hardcoded mock businesses - now using real data from hybridBusinessSearchService

  useEffect(() => {
    (async () => {
      const profileZip = await loadUserZipcode();
      maybeShowZipcodePrompt(profileZip);
    })();
    loadFavorites();
    
    // Check if there's a preselected category from 30-second booking
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const preselectedCategory = sessionStorage.getItem('preselectedCategory');
      if (preselectedCategory) {
        console.log('🔍 BusinessExploration: Found preselected category from 30-second booking:', preselectedCategory);
        setSelectedCategory(preselectedCategory);
        // Clear it so it doesn't persist
        sessionStorage.removeItem('preselectedCategory');
      }
    }
  }, []);

  // Auto-load category when zipcode is available AND a specific category is selected (not "all")
  // Don't auto-load "all" categories to avoid unnecessary Foursquare API calls
  useEffect(() => {
    // Only auto-load if:
    // 1. User has zipcode
    // 2. A specific category is selected (not "all")
    // 3. Category was preselected from 30-second booking (preselectedCategory exists) OR user manually selected it
    if (userZipcode && selectedCategory && selectedCategory !== 'all') {
      console.log('🔍 BusinessExploration: Auto-loading category for zipcode:', userZipcode, 'category:', selectedCategory);
      loadBusinesses(selectedCategory, '', searchRadius);
    }
    // Don't auto-load "all" - wait for user to explicitly select it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userZipcode, selectedCategory]);

  const loadUserZipcode = async (): Promise<string | null> => {
    try {
      // Same as web: use UnifiedAuthService.getCurrentUser() (Firestore users/{uid}) on both platforms
      const { UnifiedAuthService } = await import('../services/unifiedAuthService');
      const user = await UnifiedAuthService.getCurrentUser();

      if (user && user.address) {
        let zipcode: string | null = null;
        let countryFromAddr = '';
        if (typeof user.address === 'object' && user.address.zipcode) {
          zipcode = user.address.zipcode;
          countryFromAddr =
            typeof (user.address as { country?: string }).country === 'string'
              ? (user.address as { country?: string }).country!.trim()
              : '';
        } else if (typeof user.address === 'string') {
          const pin6 = user.address.match(/\b\d{6}\b/);
          const zip5 = user.address.match(/\b\d{5}\b/);
          if (pin6) zipcode = pin6[0];
          else if (zip5) zipcode = zip5[0];
        }
        if (countryFromAddr) {
          setUserCountryForLocation(countryFromAddr);
        }
        if (zipcode) {
          setUserZipcode(zipcode);
          console.log('✅ BusinessExploration: Loaded user zipcode:', zipcode);
          return zipcode;
        }
        console.warn('⚠️ BusinessExploration: No zipcode found in user address');
      } else {
        console.warn('⚠️ BusinessExploration: No user or address found');
      }

      const guestZip = await getGuestZipcodeAsync();
      if (guestZip) {
        setUserZipcode(guestZip);
        console.log('✅ BusinessExploration: Using guest session zipcode:', guestZip);
        return guestZip;
      }
      return null;
    } catch (error) {
      console.error('Error loading user zipcode:', error);
      return null;
    }
  };

  const maybeShowZipcodePrompt = (profileZip: string | null) => {
    if (profileZip) {
      return;
    }
    let skipped = false;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        skipped = sessionStorage.getItem(EXPLORE_ZIP_SKIP_KEY) === '1';
      } catch {
        skipped = false;
      }
    }
    if (!skipped) {
      setShowZipcodePrompt(true);
    }
  };

  const saveZipcodeToProfile = async (normalizedZipcode: string, country?: string) => {
    const { UnifiedAuthService } = await import('../services/unifiedAuthService');
    const user = await UnifiedAuthService.getCurrentUser();
    if (!user?.id) {
      throw new Error('Sign in to save your location.');
    }

    const existing =
      user.address && typeof user.address === 'object'
        ? (user.address as {
            street?: string;
            city?: string;
            state?: string;
            zipcode?: string;
            country?: string;
          })
        : {};

    const addressObject = {
      street: existing.street || '',
      city: existing.city || '',
      state: existing.state || '',
      zipcode: normalizedZipcode,
      country: country || existing.country || userCountryForLocation || 'USA',
    };

    const updatedUser = {
      ...user,
      address: addressObject,
    };

    if (Platform.OS === 'web') {
      const { doc, updateDoc } = require('firebase/firestore');
      const { getWebDb, initializeFirebase } = require('../services/unifiedAuthService');
      const initialized = initializeFirebase();
      if (!initialized) {
        throw new Error('Could not connect. Please try again.');
      }
      const db = getWebDb();
      if (!db) {
        throw new Error('Could not connect. Please try again.');
      }
      await updateDoc(doc(db, 'users', user.id), { address: addressObject });
      try {
        const ProductionStorage = (await import('../utils/productionStorage')).default;
        await ProductionStorage.setItem(
          'user',
          JSON.stringify({ ...updatedUser, lastLogin: new Date().toISOString() }),
        );
      } catch {
        /* ignore cache sync */
      }
    } else {
      const { AuthManager } = await import('../utils/authManager');
      await AuthManager.saveUser(updatedUser as any);
    }

    setUserCountryForLocation(addressObject.country);
    setUserZipcode(normalizedZipcode);
    setSearchZipcodeOverride(null);
  };

  const handleSavePromptZipcode = async () => {
    const trimmed = promptZipcode.trim();
    if (!trimmed) {
      setPromptZipcodeError('Enter your ZIP or postal code');
      return;
    }
    const { geocodingService } = await import('../services/geocodingService');
    const normalized = geocodingService.normalizeZipcodeForStorage(
      trimmed,
      userCountryForLocation || undefined,
    );
    if (!normalized) {
      setPromptZipcodeError(
        'Enter a valid US ZIP (5 digits), Canadian postal code (e.g. A1A 1A1), or India PIN (6 digits).',
      );
      return;
    }

    setPromptZipcodeError(null);
    setSavingPromptZipcode(true);
    try {
      const { UnifiedAuthService } = await import('../services/unifiedAuthService');
      const user = await UnifiedAuthService.getCurrentUser();
      if (guestMode && !user?.id) {
        await setGuestZipcodeAsync(normalized);
        setUserZipcode(normalized);
        setShowZipcodePrompt(false);
        setPromptZipcode('');
        if (selectedCategory) {
          loadBusinesses(selectedCategory, searchQuery, searchRadius, normalized);
        }
        return;
      }
      await saveZipcodeToProfile(normalized);
      setShowZipcodePrompt(false);
      setPromptZipcode('');
      if (selectedCategory) {
        loadBusinesses(selectedCategory, searchQuery, searchRadius, normalized);
      }
    } catch (error: any) {
      setPromptZipcodeError(error?.message || 'Could not save your location. Try again.');
    } finally {
      setSavingPromptZipcode(false);
    }
  };

  const handleSkipZipcodePrompt = () => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        sessionStorage.setItem(EXPLORE_ZIP_SKIP_KEY, '1');
      } catch {
        /* ignore */
      }
    }
    setShowZipcodePrompt(false);
    setPromptZipcodeError(null);
  };

  const loadFavorites = async () => {
    try {
      const favorites = await favoritesService.getFavoriteBusinesses();
      const favoriteIds = new Set(favorites.map(fav => fav.id));
      setFavoriteBusinessIds(favoriteIds);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const handleOpenChangeArea = () => {
    setChangeAreaZipcode(searchZipcodeOverride || userZipcode || '');
    setChangeAreaError(null);
    setShowChangeAreaModal(true);
  };

  const handleApplyChangeArea = async () => {
    const trimmed = changeAreaZipcode.trim();
    if (!trimmed) {
      setChangeAreaError('Enter a zipcode or postal code');
      return;
    }
    const { geocodingService } = await import('../services/geocodingService');
    const normalized = geocodingService.normalizeZipcodeForStorage(
      trimmed,
      userCountryForLocation || undefined
    );
    if (!normalized) {
      setChangeAreaError(
        'Enter a valid US ZIP (5 digits), Canadian postal code (e.g. A1A 1A1), or India PIN (6 digits).'
      );
      return;
    }
    setChangeAreaError(null);
    setSearchZipcodeOverride(normalized);
    setShowChangeAreaModal(false);
    if (selectedCategory) {
      loadBusinesses(selectedCategory, searchQuery, searchRadius, normalized);
    }
  };

  const handleUseProfileLocation = () => {
    setSearchZipcodeOverride(null);
    setShowChangeAreaModal(false);
    if (selectedCategory) {
      loadBusinesses(selectedCategory, searchQuery, searchRadius, userZipcode || undefined);
    }
  };

  const handleCategorySelect = useCallback((categoryId: string) => {
    setSelectedCategory(categoryId);
    // Always pass current radius when loading businesses
    // Access current state values via refs or direct call
    loadBusinesses(categoryId, searchQuery, searchRadius);
  }, [searchQuery, searchRadius]);

  // Debounce search to avoid too many API calls
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Only search if category is selected and query has at least 2 characters
    if (selectedCategory && query.trim().length >= 2) {
      // Debounce: wait 500ms after user stops typing
      searchTimeoutRef.current = setTimeout(() => {
        loadBusinesses(selectedCategory, query, searchRadius);
      }, 500);
    } else if (selectedCategory && query.trim().length === 0) {
      // If query is cleared, reload without search query
      loadBusinesses(selectedCategory, '', searchRadius);
    }
  }, [selectedCategory, searchRadius]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const loadBusinesses = async (categoryId: string, userSearchQuery?: string, radiusOverride?: number, optionalZipcodeForThisCall?: string) => {
    setLoading(true);
    try {
      // When traveling: use override zipcode; optional param overrides state for this call
      let zipcodeForSearch = optionalZipcodeForThisCall ?? searchZipcodeOverride ?? userZipcode;
      
      // Use radiusOverride if provided, otherwise use current state
      const currentRadius = radiusOverride !== undefined ? radiusOverride : searchRadius;
      
      let countryForGeocode: string | undefined =
        userCountryForLocation.trim() || undefined;

      // If no zipcode in state, resolve from Firestore (same as web: UnifiedAuthService.getCurrentUser)
      if (!zipcodeForSearch) {
        try {
          const { UnifiedAuthService } = await import('../services/unifiedAuthService');
          const currentUser = await UnifiedAuthService.getCurrentUser();
          if (currentUser?.address) {
            if (typeof currentUser.address === 'object' && currentUser.address.zipcode) {
              zipcodeForSearch = currentUser.address.zipcode;
              const c = (currentUser.address as { country?: string }).country?.trim();
              if (c) {
                countryForGeocode = c;
                setUserCountryForLocation(c);
              }
            } else if (typeof currentUser.address === 'string') {
              const m6 = currentUser.address.match(/\b\d{6}\b/);
              const m5 = currentUser.address.match(/\b\d{5}\b/);
              if (m6) zipcodeForSearch = m6[0];
              else if (m5) zipcodeForSearch = m5[0];
            }
          }
          if (zipcodeForSearch) setUserZipcode(zipcodeForSearch);
        } catch (error) {
          console.error('Error getting user zipcode:', error);
        }
      }
      
      // If still no zipcode, show empty (don't show mock data)
      if (!zipcodeForSearch) {
        console.log('⚠️ No user zipcode found, cannot load businesses');
        setBusinesses([]);
        setLoading(false);
        return;
      }
      
      // Load real businesses from hybrid search service
      const { hybridBusinessSearchService } = await import('../services/hybridBusinessSearchService');
      
      // Use user's search query if provided, otherwise use category-based query
      // If user has typed a search query, use it; otherwise use category logic
      let finalQuery = '';
      if (userSearchQuery && userSearchQuery.trim()) {
        finalQuery = userSearchQuery.trim();
        // If user searches for "haircut" or similar, ensure we're using beauty_wellness category
        const searchLower = finalQuery.toLowerCase();
        if ((searchLower.includes('hair') || searchLower.includes('haircut') || searchLower.includes('salon') || 
             searchLower.includes('beauty') || searchLower.includes('spa') || searchLower.includes('nail')) &&
            categoryId !== 'beauty_wellness' && categoryId !== 'all') {
          // Auto-switch to beauty_wellness category if searching for hair/beauty related terms
          console.log('🔍 Auto-detected beauty/wellness search, using beauty_wellness category');
          categoryId = 'beauty_wellness';
        }
      } else if (categoryId === 'all') {
        finalQuery = 'all';
      } else {
        // For specific categories, use category name as query to help Foursquare find relevant businesses
        const selectedCategory = BUSINESS_CATEGORIES.find(cat => cat.id === categoryId);
        if (selectedCategory) {
          // For beauty_wellness, use multiple keywords to get better results
          if (categoryId === 'beauty_wellness') {
            finalQuery = 'hair salon beauty spa'; // Use multiple keywords for better Foursquare results
          } else {
            // Use first keyword as query to help Foursquare API
            finalQuery = selectedCategory.keywords[0] || '';
          }
        }
      }
      
      // Convert miles to meters for Foursquare API (1 mile = 1609.34 meters)
      const radiusInMeters = Math.round(currentRadius * 1609.34);
      
      // Step 1: Load BookAPT businesses first (instant results)
      let bookaptBusinesses: any[] = [];
      try {
        // Get user location for distance calculation (cached by geocodingService)
        let userLocation: { lat: number; lng: number } | undefined;
        try {
          const { geocodingService } = await import('../services/geocodingService');
          const userLoc = await geocodingService.zipcodeToLatLng(
            zipcodeForSearch,
            countryForGeocode
          );
          if (userLoc) {
            userLocation = { lat: userLoc.lat, lng: userLoc.lng };
          }
        } catch (geocodeError) {
          console.warn('Could not geocode zipcode for distance calculation:', geocodeError);
        }

        bookaptBusinesses = await hybridBusinessSearchService.searchBookAPTOnly({
          query: finalQuery,
          zipcode: zipcodeForSearch,
          category: categoryId !== 'all' ? categoryId : undefined,
          radius: radiusInMeters,
          userLocation: userLocation, // Pass to avoid redundant geocoding
        });
      } catch (bookaptError) {
        console.error('Error loading BookAPT businesses:', bookaptError);
      }

      // Helper function to convert businesses to Business format
      const convertToBusinessFormat = (biz: any): Business => {
        let formattedAddress: string | undefined;
        if (biz.address || biz.city || biz.state || biz.zipcode) {
          const addressParts = [
            biz.address,
            biz.city,
            biz.state,
            biz.zipcode
          ].filter(part => part && typeof part === 'string' && part.trim().length > 0);
          formattedAddress = addressParts.length > 0 ? addressParts.join(', ') : undefined;
        }

        const distanceStr = biz.distance && biz.distance > 0 
          ? `${(biz.distance / 1609).toFixed(1)} mi` 
          : undefined;

        return {
          id: biz.id,
          name: biz.name || 'Unknown Business',
          address: formattedAddress,
          phone: biz.phone || '',
          email: biz.email || '',
          category: biz.category || 'Business',
          rating: biz.rating !== undefined && biz.rating !== null ? biz.rating : 4.0,
          distance: distanceStr,
          description: biz.description || undefined,
          hours: biz.hours || 'Hours not available',
          services: biz.services || [],
          amenities: biz.amenities || [],
          images: biz.images || [],
          isOpen: biz.isOpen !== undefined ? biz.isOpen : true,
          waitTime: biz.waitTime && biz.waitTime !== 'N/A' ? biz.waitTime : undefined,
          isOnBookAPT: biz.isOnBookAPT !== undefined ? biz.isOnBookAPT : (biz.source === 'bookapt'),
          source: biz.source || 'bookapt',
          city: biz.city,
          state: biz.state,
          zipcode: biz.zipcode,
          website: biz.website,
        };
      };

      // Convert BookAPT businesses and display immediately
      const bookaptBusinessList: Business[] = bookaptBusinesses.map(convertToBusinessFormat);
      
      // Display BookAPT businesses immediately (instant results)
      setBusinesses(bookaptBusinessList);
      setLoading(false); // Stop loading to show BookAPT results immediately

      // Step 2: Load Foursquare businesses (in background, after BookAPT results are shown)
      try {
        const { foursquarePlacesService } = await import('../services/foursquarePlacesService');
        const foursquareResults = await foursquarePlacesService.searchNearby({
          query: finalQuery,
          zipcode: zipcodeForSearch, // Will use cached geocoding if already geocoded
          category: categoryId !== 'all' ? categoryId : undefined,
          radius: radiusInMeters,
          limit: 50,
        });

        // Filter Foursquare businesses by category if needed
        let filteredFoursquare = foursquareResults;
        if (categoryId && categoryId !== 'all') {
          const selectedCategory = BUSINESS_CATEGORIES.find(cat => cat.id === categoryId);
          const categoryKeywords = selectedCategory?.keywords || [];
          
          if (categoryKeywords.length > 0) {
            const lowerKeywords = categoryKeywords.map(k => k.toLowerCase());
            const beautyKeywords = categoryId === 'beauty_wellness' 
              ? ['beauty', 'salon', 'spa', 'hair', 'nail', 'massage', 'facial', 'wax', 'makeup', 'skincare', 'wellness', 'aesthetic', 'cosmetic'].map(k => k.toLowerCase())
              : [];
            
            filteredFoursquare = foursquareResults.filter((biz: any) => {
              const businessCategory = (biz.category || '').toLowerCase();
              const businessName = (biz.name || '').toLowerCase();
              const businessDescription = (biz.description || '').toLowerCase();
              
              const matchesCategory = lowerKeywords.some(keyword => 
                businessCategory.includes(keyword) || businessName.includes(keyword) || businessDescription.includes(keyword)
              );
              
              if (categoryId === 'beauty_wellness' && !matchesCategory && beautyKeywords.length > 0) {
                return beautyKeywords.some(keyword => 
                  businessCategory.includes(keyword) || businessName.includes(keyword) || businessDescription.includes(keyword)
                );
              }
              
              return matchesCategory;
            });
          }
        }

        // Convert Foursquare businesses to Business format
        const foursquareBusinessList: Business[] = filteredFoursquare.map(convertToBusinessFormat);

        // Append Foursquare businesses to BookAPT businesses
        const allBusinesses = [...bookaptBusinessList, ...foursquareBusinessList];
        setBusinesses(allBusinesses);
        
        console.log(`✅ Total: ${bookaptBusinessList.length} BookAPT + ${foursquareBusinessList.length} Foursquare = ${allBusinesses.length} businesses`);
      } catch (foursquareError) {
        console.error('Error loading Foursquare businesses:', foursquareError);
        // If Foursquare fails, at least we have BookAPT businesses (already displayed)
      }
    } catch (error) {
      console.error('Error loading businesses:', error);
      // Don't show mock data on error - show empty
      setBusinesses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessPress = async (business: Business) => {
    console.log('🔍 BusinessExplorationScreen - handleBusinessPress:', {
      businessName: business.name,
      isOnBookAPT: business.isOnBookAPT,
      phone: business.phone,
    });
    if (onBusinessSelect) {
      onBusinessSelect(business);
    } else {
      console.warn('⚠️ onBusinessSelect handler is not defined!');
    }
  };

  const handleOpenRequestForm = (business: Business, type: 'appointment' | 'estimate', event?: any) => {
    // Stop event propagation to prevent card click
    if (event) {
      event.stopPropagation();
    }
    console.log(`📞 ${type === 'estimate' ? 'Request Estimate' : 'Request Booking'} clicked for:`, business.name);
    setRequestType(type);
    setSelectedBusinessForRequest(business);
    setShowRequestForm(true);
  };

  const handleRequestFormSubmitted = () => {
    setShowRequestForm(false);
    setSelectedBusinessForRequest(null);
    Alert.alert('Success', 'Your appointment request has been sent!');
  };

  const handleRequestFormCancel = () => {
    setShowRequestForm(false);
    setSelectedBusinessForRequest(null);
  };

  const handleToggleFavorite = async (business: Business) => {
    try {
      const isCurrentlyFavorite = favoriteBusinessIds.has(business.id);
      
      if (isCurrentlyFavorite) {
        // Remove from favorites
        const success = await favoritesService.removeFavorite(business.id);
        if (success) {
          setFavoriteBusinessIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(business.id);
            return newSet;
          });
          Alert.alert('Removed from Favorites', `${business.name} has been removed from your favorites!`);
        } else {
          Alert.alert('Error', 'Failed to remove business from favorites');
        }
      } else {
        // Add to favorites (include isOnBookAPT so Favorites knows Book vs Request Appointment)
        const favoriteBusiness = {
          id: business.id,
          ownerId: (business as any).ownerId || (business as any).businessOwnerId || business.id,
          businessId: (business as any).businessId || business.id,
          firestoreId: (business as any).firestoreId || undefined,
          docId: (business as any).docId || undefined,
          name: business.name,
          category: business.category || 'Business',
          rating: business.rating || 4.0,
          distance: business.distance || '0.0 miles', // Ensure distance is always a string
          address: business.address || '',
          phone: business.phone || 'N/A',
          imageUrl: business.images?.[0] || undefined,
          services: business.services || [],
          priceRange: '$', // Default price range since Business doesn't have this field
          hours: business.hours || 'Hours not available',
          isOnBookAPT: business.isOnBookAPT,
        };

        const success = await favoritesService.addFavorite(favoriteBusiness);
        if (success) {
          setFavoriteBusinessIds(prev => new Set(prev).add(business.id));
          Alert.alert('Added to Favorites', `${business.name} has been added to your favorites!`);
        } else {
          Alert.alert('Already in Favorites', `${business.name} is already in your favorites!`);
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      Alert.alert('Error', 'Failed to update favorites');
    }
  };

  // Debug log for Safari
  useEffect(() => {
    if (Platform.OS === 'web') {
      console.log('🔍 BusinessExplorationScreen rendered:', {
        userZipcode,
        selectedCategory,
        businessesCount: businesses.length,
        loading,
      });
    }
  }, [userZipcode, selectedCategory, businesses.length, loading]);

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={onBack}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <View style={styles.headerTitleBlock}>
          <Text style={styles.headerTitle}>Explore Businesses</Text>
        </View>
        <View style={styles.placeholder} />
      </View>

      {/* Content */}
      <ScrollView 
        style={styles.content} 
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={true}
        nestedScrollEnabled={true}
        scrollEnabled={true}
        bounces={false}
        alwaysBounceVertical={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Search Bar */}
        <View style={styles.searchContainer}>
          <View style={styles.searchInputWrapper}>
            <AppTextInput
              style={styles.searchInput}
              placeholder="Search businesses by name..."
              value={searchQuery}
              onChangeText={handleSearch}
            onSubmitEditing={() => {
              // Clear any pending debounced search
              if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
              }
              // Immediately search on submit
              if (selectedCategory) {
                loadBusinesses(selectedCategory, searchQuery, searchRadius);
              }
            }}
              returnKeyType="search"
            />
            <TouchableOpacity
              style={styles.searchButton}
              onPress={() => {
                if (selectedCategory) {
                  loadBusinesses(selectedCategory, searchQuery, searchRadius);
                } else {
                  Alert.alert('Select Category', 'Please select a category first to search for businesses.');
                }
              }}
            >
              <Text style={styles.searchButtonText}>Search</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Location Info */}
        <View style={styles.locationInfo}>
          <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <Text style={styles.locationTitle}>📍 Near {searchZipcodeOverride || userZipcode || 'Your Location'}</Text>
            {searchZipcodeOverride && (
              <Text style={[styles.locationSubtitle, { fontStyle: 'italic' }]}>(search area)</Text>
            )}
            <TouchableOpacity onPress={handleOpenChangeArea} style={styles.changeAreaButton}>
              <Text style={styles.changeAreaButtonText}>{searchZipcodeOverride ? 'Change area' : 'Search different area'}</Text>
            </TouchableOpacity>
          </View>
          <Text style={styles.locationSubtitle}>Showing businesses within {searchRadius} mile{searchRadius !== 1 ? 's' : ''}</Text>
          
          {/* Radius Selector */}
          <View style={styles.radiusSelector}>
            <Text style={styles.radiusLabel}>Search Radius:</Text>
            <View style={styles.radiusButtons}>
              {[1, 5, 10, 15, 25].map((miles) => (
                <TouchableOpacity
                  key={miles}
                  style={[
                    styles.radiusButton,
                    searchRadius === miles && styles.radiusButtonActive
                  ]}
                  onPress={async () => {
                    const newRadius = miles;
                    setSearchRadius(newRadius);
                    if (!selectedCategory) {
                      console.warn('⚠️ Cannot reload: No category selected');
                      return;
                    }
                    let zip = searchZipcodeOverride || userZipcode;
                    if (!zip) {
                      try {
                        const { UnifiedAuthService } = await import('../services/unifiedAuthService');
                        const currentUser = await UnifiedAuthService.getCurrentUser();
                        if (currentUser?.address) {
                          if (typeof currentUser.address === 'object' && currentUser.address.zipcode) zip = currentUser.address.zipcode;
                          else if (typeof currentUser.address === 'string') {
                            const m = currentUser.address.match(/\b\d{5}\b/);
                            if (m) zip = m[0];
                          }
                        }
                        if (zip) setUserZipcode(zip);
                      } catch (_) {}
                    }
                    if (zip) {
                      console.log(`🔄 Radius changed to ${newRadius} miles, reloading businesses...`);
                      loadBusinesses(selectedCategory, searchQuery, newRadius);
                    } else {
                      console.warn('⚠️ Cannot reload: No zipcode available. Add your location in Profile or use "Search different area".');
                    }
                  }}
                >
                  <Text style={[
                    styles.radiusButtonText,
                    searchRadius === miles && styles.radiusButtonTextActive
                  ]}>
                    {miles} mi
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>

        {/* Category Selection */}
        <View style={styles.categorySection}>
          <Text style={styles.sectionTitle}>Choose Category</Text>
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            style={styles.categoryScroll}
          >
            {categories.map((category) => (
              <TouchableOpacity
                key={category.id}
                style={[
                  styles.categoryCard,
                  selectedCategory === category.id && styles.categoryCardSelected
                ]}
                onPress={() => handleCategorySelect(category.id)}
              >
                <Text style={styles.categoryIcon}>{category.icon}</Text>
                <Text style={[
                  styles.categoryName,
                  selectedCategory === category.id && styles.categoryNameSelected
                ]}>
                  {category.name}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {/* Businesses List */}
        <View style={styles.businessesSection}>
          <Text style={styles.sectionTitle}>
            {selectedCategory ? `${categories.find(c => c.id === selectedCategory)?.name} (${businesses.length})` : 'Select a category to explore businesses'}
          </Text>
          
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#007AFF" />
              <Text style={styles.loadingText}>Finding businesses...</Text>
            </View>
          ) : selectedCategory ? (
            <View style={styles.businessGrid}>
              {businesses.map((business) => (
                <View key={business.id} style={[styles.businessCard, { width: cardWidth as any }]}>
                  <View style={styles.businessCardContent}>
                    <View style={styles.businessInfo}>
                      <View style={styles.businessNameRow}>
                        <Text style={styles.businessName} numberOfLines={2}>
                          {business.name || 'Unknown Business'}
                        </Text>
                      </View>
                      <View style={styles.businessCardActions}>
                        {business.isOnBookAPT === true ? (
                          <TouchableOpacity
                            style={styles.cardActionBtn}
                            accessibilityLabel="Book"
                            accessibilityRole="button"
                            onPress={(event) => {
                              event?.stopPropagation?.();
                              console.log('📅 Book Appointment button clicked for:', business.name);
                              if (onBookBusiness) {
                                onBookBusiness(business);
                              } else if (onBusinessSelect) {
                                onBusinessSelect(business);
                              } else {
                                console.warn('⚠️ No booking handler available!');
                              }
                            }}
                          >
                            <Text style={styles.cardActionBtnText} numberOfLines={2}>
                              Book
                            </Text>
                          </TouchableOpacity>
                        ) : (
                          <TouchableOpacity
                            style={styles.cardActionBtn}
                            accessibilityLabel="Request booking"
                            accessibilityRole="button"
                            activeOpacity={0.8}
                            onPress={(e) => handleOpenRequestForm(business, 'appointment', e)}
                          >
                            <Text style={styles.cardActionBtnText} numberOfLines={2}>
                              Request Booking
                            </Text>
                          </TouchableOpacity>
                        )}
                        <TouchableOpacity
                          style={styles.cardActionBtn}
                          accessibilityLabel="Request estimate"
                          accessibilityRole="button"
                          activeOpacity={0.8}
                          onPress={(e) => handleOpenRequestForm(business, 'estimate', e)}
                        >
                          <Text style={styles.cardActionBtnText} numberOfLines={2}>
                            {business.isOnBookAPT === true ? 'Estimate' : 'Request Estimate'}
                          </Text>
                        </TouchableOpacity>
                      </View>
                      {business.category && typeof business.category === 'string' && business.category.trim().length > 0 && (
                        <Text style={styles.businessCategory}>{business.category}</Text>
                      )}
                      {business.address && typeof business.address === 'string' && business.address.trim().length > 0 && (
                        <Text style={styles.businessAddress}>{business.address}</Text>
                      )}
                      {business.description && typeof business.description === 'string' && business.description.trim().length > 0 && (
                        <Text style={styles.businessDescription}>{business.description}</Text>
                      )}
                      <View style={styles.businessDetails}>
                        <View style={styles.businessRating}>
                          {business.rating !== undefined && business.rating !== null && (
                            <Text style={styles.ratingText}>⭐ {business.rating}</Text>
                          )}
                          {business.distance && typeof business.distance === 'string' && business.distance.trim().length > 0 && business.distance !== 'N/A' && (
                            <Text style={styles.distanceText}>{business.distance}</Text>
                          )}
                        </View>
                      </View>
                    </View>
                  </View>
                  
                  {/* Favorite Button */}
                  <View style={styles.favoriteButtonContainer}>
                    <TouchableOpacity
                      style={[
                        styles.favoriteButton,
                        favoriteBusinessIds.has(business.id) && styles.favoriteButtonActive
                      ]}
                      onPress={() => handleToggleFavorite(business)}
                    >
                      <Text style={[
                        styles.favoriteButtonText,
                        favoriteBusinessIds.has(business.id) && styles.favoriteButtonTextActive
                      ]}>
                        {favoriteBusinessIds.has(business.id) ? '❤️' : '🤍'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ))}
            </View>
          ) : (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateIcon}>🔍</Text>
              <Text style={styles.emptyStateTitle}>Choose a Category</Text>
              <Text style={styles.emptyStateSubtitle}>
                Select a business category above to discover nearby businesses
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
      
      {/* First-time Explore: profile has no zipcode (common after widget signup) */}
      <Modal
        visible={showZipcodePrompt}
        animationType="slide"
        transparent={true}
        onRequestClose={handleSkipZipcodePrompt}
        statusBarTranslucent
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, styles.changeAreaModalContent]}>
            <Text style={styles.changeAreaModalTitle}>Where are you looking?</Text>
            <Text style={styles.changeAreaModalSubtitle}>
              Add your ZIP or postal code so we can show businesses near you. We save it to your profile for future searches.
            </Text>
            <AppTextInput
              style={[styles.searchInput, styles.changeAreaInput]}
              placeholder="e.g. 90210 or A1A 1A1"
              value={promptZipcode}
              onChangeText={(t) => {
                setPromptZipcode(t);
                setPromptZipcodeError(null);
              }}
              autoCapitalize="characters"
              maxLength={7}
              autoComplete="postal-code"
              textContentType="postalCode"
            />
            {promptZipcodeError ? (
              <Text style={styles.changeAreaError}>{promptZipcodeError}</Text>
            ) : null}
            <View style={styles.changeAreaButtons}>
              <TouchableOpacity
                style={styles.changeAreaPrimaryButton}
                onPress={handleSavePromptZipcode}
                disabled={savingPromptZipcode}
              >
                {savingPromptZipcode ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={styles.changeAreaPrimaryButtonText}>Save & find businesses</Text>
                )}
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.changeAreaCancelButton}
                onPress={handleSkipZipcodePrompt}
                disabled={savingPromptZipcode}
              >
                <Text style={styles.changeAreaCancelButtonText}>Not now</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Change search area modal (e.g. when traveling) */}
      <Modal
        visible={showChangeAreaModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowChangeAreaModal(false)}
        statusBarTranslucent
      >
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={[StyleSheet.absoluteFill, styles.modalBackdrop]}
            activeOpacity={1}
            onPress={() => setShowChangeAreaModal(false)}
          >
            <View
              style={[styles.modalContent, styles.changeAreaModalContent]}
              onStartShouldSetResponder={() => true}
            >
              <Text style={styles.changeAreaModalTitle}>Search in a different area</Text>
              <Text style={styles.changeAreaModalSubtitle}>Enter a zipcode or postal code to find businesses there (e.g. when traveling). Your profile address is not changed.</Text>
              <AppTextInput
                style={[styles.searchInput, styles.changeAreaInput]}
                placeholder="e.g. 90210 or A1A 1A1"
                value={changeAreaZipcode}
                onChangeText={(t) => { setChangeAreaZipcode(t); setChangeAreaError(null); }}
                autoCapitalize="characters"
                maxLength={7}
              />
              {changeAreaError ? <Text style={styles.changeAreaError}>{changeAreaError}</Text> : null}
              <View style={styles.changeAreaButtons}>
                <TouchableOpacity style={styles.changeAreaPrimaryButton} onPress={handleApplyChangeArea}>
                  <Text style={styles.changeAreaPrimaryButtonText}>Update search area</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.changeAreaSecondaryButton} onPress={handleUseProfileLocation}>
                  <Text style={styles.changeAreaSecondaryButtonText}>Use my profile location</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.changeAreaCancelButton} onPress={() => setShowChangeAreaModal(false)}>
                  <Text style={styles.changeAreaCancelButtonText}>Cancel</Text>
                </TouchableOpacity>
              </View>
            </View>
          </TouchableOpacity>
        </View>
      </Modal>

      {/* Appointment Request Form Modal (same as web: full request form) */}
      <Modal
        visible={showRequestForm && selectedBusinessForRequest !== null}
        animationType="slide"
        transparent={true}
        onRequestClose={handleRequestFormCancel}
        statusBarTranslucent
      >
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={[StyleSheet.absoluteFill, styles.modalBackdrop]}
            activeOpacity={1}
            onPress={handleRequestFormCancel}
          >
            <View
              style={styles.modalContent}
              onStartShouldSetResponder={() => true}
            >
              {selectedBusinessForRequest ? (
                requestType === 'estimate' &&
                isTransportationBusinessCategory(selectedBusinessForRequest.category) ? (
                  <TransportationQuoteRequestForm
                    business={selectedBusinessForRequest}
                    onCancel={handleRequestFormCancel}
                    onSubmit={handleRequestFormSubmitted}
                  />
                ) : (
                  <AppointmentRequestForm
                    business={selectedBusinessForRequest}
                    requestType={requestType}
                    onCancel={handleRequestFormCancel}
                    onSubmit={handleRequestFormSubmitted}
                  />
                )
              ) : null}
            </View>
          </TouchableOpacity>
        </View>
      </Modal>

      {/* Bottom Menu */}
      {guestMode ? (
        <View style={styles.guestFooter}>
          <TouchableOpacity style={styles.guestFooterBack} onPress={onBack}>
            <Text style={styles.guestFooterBackText}>← Back</Text>
          </TouchableOpacity>
          {onSignIn ? (
            <TouchableOpacity style={styles.guestFooterSignIn} onPress={onSignIn}>
              <Text style={styles.guestFooterSignInText}>Sign in</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : (
        <UserBottomMenu
          activeTab="business-exploration"
          onTabPress={onTabPress}
          onLogout={onLogout}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    ...(Platform.OS === 'web' && {
      height: '100vh' as any,
      maxHeight: '100vh' as any,
      minHeight: '100vh' as any, // Safari needs explicit min-height
      overflowY: 'hidden' as any, // Let ScrollView handle scrolling
      overflowX: 'hidden' as any,
      position: 'relative' as any,
      display: 'flex' as any, // Ensure flexbox works in Safari
      flexDirection: 'column' as any,
      WebkitTransform: 'translateZ(0)' as any, // Force hardware acceleration
      transform: 'translateZ(0)' as any,
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 60,
    paddingBottom: 20,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  backButton: {
    padding: 8,
  },
  backButtonText: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '600',
  },
  headerTitleBlock: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  placeholder: {
    width: 60,
  },
  content: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      WebkitOverflowScrolling: 'touch' as any, // iOS Safari smooth scrolling
      overflowY: 'auto' as any,
      overflowX: 'hidden' as any,
      height: '100%' as any,
      maxHeight: '100%' as any,
      // Enable touch scrolling on mobile browsers
      touchAction: 'pan-y' as any,
      overscrollBehavior: 'contain' as any,
      // Safari-specific fixes
      WebkitTransform: 'translateZ(0)' as any, // Force hardware acceleration
      transform: 'translateZ(0)' as any,
      willChange: 'scroll-position' as any,
    }),
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingBottom: 70, // Space for bottom menu (consistent across all screens)
    flexGrow: 1,
    ...(Platform.OS === 'web' ? {
      minHeight: 'calc(100vh - 140px)' as any, // Safari-compatible min-height
    } : {
      minHeight: '100%', // Ensure content takes full height even when empty
    }),
  },
  searchContainer: {
    marginTop: 20,
    marginBottom: 16,
  },
  searchInputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    ...(Platform.OS === 'web' ? {
      gap: 10,
    } : {
      // Fallback for older Safari versions that don't support gap
      marginRight: 10,
    }),
  },
  searchInput: {
    flex: 1,
    backgroundColor: 'white',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    borderWidth: 1,
    borderColor: '#e1e5e9',
    ...(Platform.OS === 'web' ? {
      marginRight: 10, // Safari fallback for gap
    } : {}),
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  searchButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  searchButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  locationInfo: {
    backgroundColor: 'white',
    padding: 16,
    borderRadius: 12,
    marginTop: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  locationTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  locationSubtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
  },
  changeAreaButton: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    backgroundColor: '#e8f4fd',
    borderRadius: 8,
  },
  changeAreaButtonText: {
    fontSize: 14,
    color: '#007AFF',
    fontWeight: '600',
  },
  changeAreaModalContent: {
    maxWidth: 400,
    width: '90%',
    padding: 24,
  },
  changeAreaModalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  changeAreaModalSubtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
    lineHeight: 20,
  },
  changeAreaInput: {
    marginBottom: 8,
  },
  changeAreaError: {
    fontSize: 13,
    color: '#c00',
    marginBottom: 12,
  },
  changeAreaButtons: {
    gap: 10,
    marginTop: 8,
  },
  changeAreaPrimaryButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 12,
    borderRadius: 12,
    alignItems: 'center',
  },
  changeAreaPrimaryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  changeAreaSecondaryButton: {
    backgroundColor: '#f0f0f0',
    paddingVertical: 12,
    borderRadius: 12,
    alignItems: 'center',
  },
  changeAreaSecondaryButtonText: {
    color: '#333',
    fontSize: 16,
  },
  changeAreaCancelButton: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  changeAreaCancelButtonText: {
    color: '#666',
    fontSize: 15,
  },
  radiusSelector: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e1e5e9',
  },
  radiusLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  radiusButtons: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  radiusButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#e1e5e9',
  },
  radiusButtonActive: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  radiusButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#666',
  },
  radiusButtonTextActive: {
    color: 'white',
  },
  categorySection: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  categoryScroll: {
    marginHorizontal: -20,
    paddingHorizontal: 20,
  },
  categoryCard: {
    alignItems: 'center',
    backgroundColor: 'white',
    padding: 16,
    borderRadius: 12,
    marginRight: 12,
    minWidth: 100,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  categoryCardSelected: {
    backgroundColor: '#007AFF',
  },
  categoryIcon: {
    fontSize: 24,
    marginBottom: 8,
  },
  categoryName: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
    textAlign: 'center',
  },
  categoryNameSelected: {
    color: 'white',
  },
  businessesSection: {
    marginBottom: 20,
  },
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  businessGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 12,
    justifyContent: 'flex-start',
    gap: 12,
  },
  businessCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    overflow: 'hidden',
    position: 'relative',
  },
  businessCardContent: {
    padding: 16,
  },
  businessInfo: {
    flex: 1,
    paddingRight: 40,
  },
  businessNameRow: {
    marginBottom: 8,
  },
  businessName: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#333',
    lineHeight: 20,
  },
  businessCardActions: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'stretch',
    alignSelf: 'stretch',
    width: '100%',
    marginTop: 8,
    marginHorizontal: -3,
  },
  cardActionBtn: {
    flex: 1,
    minWidth: 0,
    marginHorizontal: 3,
    height: 44,
    paddingHorizontal: 4,
    borderRadius: 8,
    backgroundColor: '#2563EB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardActionBtnText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '600',
    textAlign: 'center',
    lineHeight: 12,
  },
  businessCategory: {
    fontSize: 14,
    color: '#007AFF',
    marginBottom: 4,
  },
  businessAddress: {
    fontSize: 12,
    color: '#666',
    marginBottom: 8,
  },
  businessDescription: {
    fontSize: 13,
    color: '#555',
    marginBottom: 12,
    lineHeight: 18,
  },
  businessDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  businessRating: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  ratingText: {
    fontSize: 12,
    color: '#333',
  },
  distanceText: {
    fontSize: 12,
    color: '#666',
  },
  businessStatus: {
    alignItems: 'flex-end',
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
  },
  statusOpen: {
    color: '#28a745',
  },
  statusClosed: {
    color: '#dc3545',
  },
  waitTimeText: {
    fontSize: 11,
    color: '#666',
    marginTop: 2,
  },
  favoriteButtonContainer: {
    position: 'absolute',
    top: 16,
    right: 16,
  },
  favoriteButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f8f9fa',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e1e5e9',
  },
  favoriteButtonActive: {
    backgroundColor: '#ffe6e6',
    borderColor: '#ff6b6b',
  },
  favoriteButtonText: {
    fontSize: 16,
  },
  favoriteButtonTextActive: {
    fontSize: 16,
  },
  businessActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
  },
  requestButton: {
    flex: 1,
    backgroundColor: '#007AFF',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  requestButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  bookButton: {
    flex: 1,
    backgroundColor: '#007AFF',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  bookButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalBackdrop: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 12,
    width: '100%',
    maxWidth: 500,
    maxHeight: '90%',
    minHeight: 400,
    overflow: 'hidden',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 20,
  },
  emptyStateIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  emptyStateSubtitle: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    lineHeight: 22,
  },
  guestFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#e1e5e9',
    backgroundColor: '#fff',
  },
  guestFooterBack: {
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  guestFooterBackText: {
    fontSize: 16,
    color: '#1d3557',
    fontWeight: '600',
  },
  guestFooterSignIn: {
    backgroundColor: '#e76f51',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  guestFooterSignInText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 15,
  },
});

export default BusinessExplorationScreen;
